import React from 'react';
import { LegalPageShell, Section, Callout } from '../components/shared/LegalPageShell';

/**
 * TermsPage — Stage 20B+ Wave A · /terms
 *
 * Clear, human, not lawyerly. Explains the boundaries
 * (what Kindra does, what it doesn't) before the rules.
 */
const TermsPage = () => (
  <LegalPageShell
    eyebrow="Terms of service"
    title="The basics of using Kindra."
    subtitle="These are the rules that let us run Kindra responsibly and let you use it with confidence. Short, direct, nothing hidden."
    updated="30 April 2026"
    current="/terms"
  >
    <Section eyebrow="Read this first" title="Kindra is not a clinician.">
      <p>
        Kindra explains, organises and remembers your health information. It helps you walk into appointments better prepared. It does not diagnose, prescribe, or replace care from a qualified clinician — and it should not be used in an emergency.
      </p>
      <p>
        If you're experiencing a medical emergency, call <strong>000</strong> in Australia or your local emergency number.
      </p>
    </Section>

    <Section title="What you're agreeing to">
      <p>
        By creating an account you're agreeing that:
      </p>
      <ul className="space-y-2 list-disc pl-5">
        <li>You're 18 or older.</li>
        <li>The information you add is yours to share (or you have permission to share it).</li>
        <li>You'll use Kindra as a companion tool — not as a substitute for medical advice.</li>
        <li>You're responsible for the accounts you create and keeping your sign-in details private.</li>
      </ul>
    </Section>

    <Section title="Your account">
      <p>
        One account, one person. Please don't share sign-in details. If you think someone else has accessed your account, change your password and email us immediately at hello@kindraai.dev.
      </p>
    </Section>

    <Section title="Payment, trial and cancellation">
      <p>
        Kindra starts with a free trial. You can cancel anytime from the Billing page inside the app. When you cancel, you keep access until the end of your current paid period — we don't pro-rate refunds on partial periods but we also don't charge you again.
      </p>
      <p>
        Payments are processed securely by Stripe. We never see or store your card number.
      </p>
    </Section>

    <Callout testid="terms-boundaries-callout">
      <p className="font-medium mb-3">What Kindra is not:</p>
      <ul className="space-y-1.5 list-disc pl-5">
        <li>A diagnostic tool. It explains what a report says; it doesn't tell you what's wrong with you.</li>
        <li>A prescriber. It can help you prepare questions; it cannot recommend a specific medication or dose.</li>
        <li>An emergency service. Never rely on Kindra in a crisis — call 000 or your local emergency number.</li>
        <li>A replacement for your doctor, specialist, or allied health professional.</li>
      </ul>
    </Callout>

    <Section title="How we may suspend access">
      <p>
        We may pause or close accounts that abuse the service, violate these terms, or put others at risk. If that happens, we'll email you the reason and give you an opportunity to respond — unless doing so is unsafe.
      </p>
    </Section>

    <Section title="Liability, in plain English">
      <p>
        Kindra provides information, not medical advice. We do our best to give you accurate, useful context, but we cannot guarantee clinical outcomes, and we aren't responsible for decisions made without a qualified clinician's input. To the fullest extent allowed by Australian law, our liability to you for any claim connected with Kindra is limited to the amount you've paid us in the previous 12 months.
      </p>
    </Section>

    <Section title="Changes to these terms">
      <p>
        We'll email every active user before making material changes. The latest version will always live at this URL.
      </p>
    </Section>

    <Section title="Questions">
      <p>
        Email us at <a href="mailto:hello@kindraai.dev" className="underline underline-offset-2 hover:text-[#0E1C2E]" style={{ color: '#0E1C2E' }}>hello@kindraai.dev</a>.
      </p>
    </Section>
  </LegalPageShell>
);

export default TermsPage;
